// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, User, Phone, AlertTriangle, Heart, PawPrint, Droplet, Truck } from 'lucide-react'
import * as api from '../../api/client'
import { useDrugRefValues } from '../../hooks/useDrugRefValues'
import { useRxSideValuesAll } from '../../hooks/useRxSideValuesAll'
import CategoryBadge from '../../components/common/CategoryBadge'
import { formatPhone, formatDob, formatCell } from '../../api/format'
import { friendlyError } from '../../api/errors'
import { ClinicalStatePill, PayerStatePill, ErrorBanner, LoadingState, EmptyState, relativeTime } from '../../components/workflow/StatePills'
import { usePaged, Pager, DEFAULT_PAGE_SIZE } from '../../components/Pager'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'
import PatientShipmentsStrip from './PatientShipmentsStrip'
import PatientCareRxStrip from './PatientCareRxStrip'
import PatientTherapyLanes from './PatientTherapyLanes'
import PatientComments from '../../components/workflow/PatientComments'
import PatientDocuments from './PatientDocuments'
import PatientEnrollmentPdfButton from './PatientEnrollmentPdfButton'
import CustomFieldInput from '../../components/CustomFieldInput'
import { useAuth } from '../../auth/AuthContext'
import { usePatientSiteOfCare } from '../../hooks/queries/siteOfCare'
import { useModules } from '../../auth/ModulesContext'
import OtherEmailsSection from './OtherEmailsSection'

export default function PatientDetailPage() {
  const { id } = useParams<{ id: string }>()
  const psocQ = usePatientSiteOfCare(id)   // D-SOC-2: unconditional, above the early returns
  const navigate = useNavigate()
  const { hasPermission, me } = useAuth()
  // ASP #26: alternate emails are a CareRx feature (mig 420) — shown where CareRx is on and allowed.
  const { isDisabled } = useModules()
  const showOtherEmails = hasPermission('carerx.access') && !isDisabled('carerx')
  const [data, setData] = useState<api.PatientFullResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [layout, setLayout] = useState<api.RxViewLayout | null>(null)
  const [cfv, setCfv] = useState<api.CustomFieldValues | null>(null)
  const [emv, setEmv] = useState<api.EntityMappedValues | null>(null)

  useEffect(() => { api.getRxViewLayout(undefined, 'patient').then(setLayout).catch(() => setLayout(null)) }, [])
  const [rxCols, setRxCols] = useState<api.RxViewLayout | null>(null)
  useEffect(() => { api.getRxViewLayout(undefined, 'patient_rx_list').then(setRxCols).catch(() => setRxCols(null)) }, [])
  useEffect(() => { if (id) api.getCustomFieldValues('patient', id).then(setCfv).catch(() => setCfv(null)) }, [id])
  useEffect(() => { if (id) api.getEntityMappedValues('patient', id).then(setEmv).catch(() => setEmv(null)) }, [id])

  useEffect(() => {
    if (!id) return
    setLoading(true); setError(null)
    api.getPatient(id)
      .then(setData)
      .catch(e => setError(friendlyError(e)))
      .finally(() => setLoading(false))
  }, [id])

  if (loading) return <div style={pageStyle}><LoadingState label="Loading patient…" /></div>
  if (error)   return <div style={pageStyle}><ErrorBanner message={error} /></div>
  if (!data)   return null

  const { patient, rx } = data
  const activeRx = rx.filter(r => !['delivered','cancelled','rejected'].includes(r.state))
  const historicalRx = rx.filter(r => ['delivered','cancelled','rejected'].includes(r.state))

  // Custom layout rendering (opt-in). Mirrors the hardcoded Demographics/Contact/Clinical
  // cards; a pharmacy keeps the built-in view until it saves a Patient layout in the builder.
  const cardIcon = (key: string): any =>
    ({ demographics: User, contact: Phone, clinical: Heart } as any)[key] ?? User
  const layoutFieldValue = (f: any): string => {
    const col = f.owlrx_column
    // D-SOC-2: virtual table backed by GET /patients/{id}/site-of-care (page-level hook below).
    if (f.owlrx_table === 'patient_site_of_care') {
      const c = psocQ.data?.current
      if (!c) return psocQ.isLoading ? '…' : '—'
      if (col === 'facility_type') return c.facility_type_label.split(' — ')[0]
      if (col === 'facility_name') return c.facility_name ?? (c.facility_type === 'residential' ? 'Patient residence' : '—')
      if (col === 'facility_phone') return formatPhone(c.facility_phone)
      if (col === 'contact_name') return c.contact_name ?? '—'
      if (col === 'contact_phone') return formatPhone(c.contact_phone)
      if (col === 'contact_email') return c.contact_email ?? '—'
      if (col === 'operating_hours') return c.operating_hours ?? '—'
      if (col === 'delivery_cutoff_time') return c.delivery_cutoff_time ?? '—'
      return '—'
    }
    if (col === 'display_name') return (patient.display_name as string) ?? '—'
    if (col === 'gender') return (patient.gender as string) ?? (patient.sex as string) ?? '—'
    if (col === 'date_of_birth') return formatDob(patient.date_of_birth as string)
    if (col.includes('phone') || col.includes('fax')) return formatPhone((patient as any)[col] as string)
    if (col === 'height_inches') return patient.height_inches != null ? `${patient.height_inches}″` : '—'
    if (col === 'weight_ounces') return patient.weight_ounces != null ? `${(Number(patient.weight_ounces) / 16).toFixed(1)} lb` : '—'
    if (col === 'allergies') return (patient.allergies as string) ?? 'None on file'
    if (col === 'address')
      return [patient.address_line, patient.city, patient.state_code, patient.postal_code].filter(Boolean).join(', ') || '—'
    const v = (patient as any)[col]
    return formatCell(col, v)
  }
  const myRoles = new Set(me?.authenticated ? (me.role_ids?.length ? me.role_ids : (me.primary_role_id ? [me.primary_role_id] : [])) : [])
  const isPlatformAdmin = hasPermission('platform.access')
  const regionCards = (region: string) => (layout?.cards ?? []).filter((c: any) => (c?.config?.region ?? 'body') === region)
  // D-VIEW-1 identity-band override: default (no override) keeps the band exactly as before.
  const bandAllow = api.headerBandAllow(layout?.cards as any)
  const showCore = (k: string) => !bandAllow || bandAllow.includes(k)   // existing band fields: on unless override drops them
  const showOpt  = (k: string) => !!bandAllow && bandAllow.includes(k)  // new optional fields (e.g. address): off unless override adds them
  const renderCards = (cardsList: any[]) => cardsList.map((card: any) => {
    if (!card.visible) return null
    if (!isPlatformAdmin) {
      if (Array.isArray(card.denied_roles) && card.denied_roles.some((r: string) => myRoles.has(r))) return null
      if (Array.isArray(card.allowed_roles) && card.allowed_roles.length > 0
          && !card.allowed_roles.some((r: string) => myRoles.has(r))) return null
    }
    const vis = (card.fields ?? []).filter((f: any) => f.visible)
    if (!vis.length) return null
    const Icon = cardIcon(card.card_key)
    return (
      <Section key={card.card_key} icon={Icon} title={card.title}>
        {vis.map((f: any) => <Field key={`${f.owlrx_table}.${f.owlrx_column}`} label={f.owlrx_label}>{
          f.pioneer_path
            ? (emv?.values?.[`${card.card_key}.${f.owlrx_column}`] || '—')
            : ((f.owlrx_table === 'custom')
                ? <CustomFieldInput surface="patient" recordId={id || ''} cardKey={card.card_key} field={f}
                    initialValue={cfv?.values?.[`${card.card_key}.${f.owlrx_column}`]} canWrite={!!cfv?.can_write} canModify={!!cfv?.can_modify} />
                : layoutFieldValue(f))
        }</Field>)}
      </Section>
    )
  })

  // D-VIEW-1: render a customized header in the SAME compact band style as the
  // built-in band (name + MRN + status badge, inline meta row), driven by the
  // composed header fields. Safety/note alerts always render.
  const renderHeaderBand = (headerCards: any[]) => {
    const fs = (headerCards || []).filter((c: any) => c.visible).flatMap((c: any) => (c.fields ?? []).filter((f: any) => f.visible))
    const nameF = fs.find((f: any) => f.owlrx_column === 'display_name')
    const statusF = fs.find((f: any) => f.owlrx_column === 'status')
    const meta = fs.filter((f: any) => !['display_name', 'mrn', 'status', 'allergies'].includes(f.owlrx_column))
    return (
      <div style={headerCard}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <h1 style={titleStyle}>
            {nameF ? layoutFieldValue(nameF) : (patient.display_name as string)}
            {patient.is_animal ? <PawPrint size={20} style={{ marginLeft: 8, color: '#a16207', verticalAlign: 'middle' }} /> : null}
          </h1>
          {statusF && patient.status && <span style={statusBadge}>{layoutFieldValue(statusF)}</span>}
          {/* Q-ID (Andrew FINAL): the Pioneer-valid Patient ID -- GUID-shaped, so shortened + copyable;
              label "Patient ID"; search stays name+DOB. Dash when absent (22 of 5,204 on ASP). */}
          {typeof patient.pioneerrx_id === 'string' && patient.pioneerrx_id ? (
            <span
              title="Tap to copy the full Patient ID"
              onClick={() => { try { navigator.clipboard.writeText(String(patient.pioneerrx_id)) } catch { /* no-op */ } }}
              style={{ fontSize: 12, color: 'var(--color-text-soft)', border: '1px solid var(--color-border)', borderRadius: 6, padding: '2px 8px', cursor: 'pointer', fontFamily: 'monospace' }}>
              Patient ID {String(patient.pioneerrx_id).slice(0, 8)}…
            </span>
          ) : (
            <span style={{ fontSize: 12, color: 'var(--color-text-soft)' }}>Patient ID —</span>
          )}
        </div>
        {meta.length > 0 && (
          <div style={{ display: 'flex', gap: 18, marginTop: 8, fontSize: 13, color: 'var(--color-text-soft)', flexWrap: 'wrap' }}>
            {meta.map((f: any) => f.owlrx_column === 'primary_phone'
              ? (patient.primary_phone ? <span key={f.owlrx_column}><Phone size={12} style={{ verticalAlign: 'middle' }} /> {layoutFieldValue(f)}</span> : null)
              : <span key={f.owlrx_column}><strong style={{ color: 'var(--color-text)' }}>{f.owlrx_label}</strong> {layoutFieldValue(f)}</span>
            )}
          </div>
        )}
        {(patient.allergies && patient.allergies !== 'None') ? (
          <div style={{ ...alertBox, background: '#fee2e2', borderColor: '#fecaca', color: '#991b1b' }}>
            <AlertTriangle size={14} aria-hidden="true" /><strong>Allergies:</strong> {patient.allergies as string}
            {patient.allergy_alert_type && <span style={mutedSm}> ({patient.allergy_alert_type as string})</span>}
          </div>
        ) : null}
        {patient.comment_critical ? (<div style={{ ...alertBox, background: '#fee2e2', borderColor: '#fecaca', color: '#991b1b' }}><AlertTriangle size={14} aria-hidden="true" /><strong>Critical:</strong> {patient.comment_critical as string}</div>) : null}
        {patient.comment_pos ? (<div style={{ ...alertBox, background: '#ecfeff', borderColor: '#a5f3fc', color: '#0e7490' }}><strong>Delivery:</strong> {patient.comment_pos as string}</div>) : null}
        {patient.comment_informational ? (<div style={{ ...alertBox, background: '#f3f4f6', borderColor: '#e5e7eb', color: '#374151' }}><strong>Note:</strong> {patient.comment_informational as string}</div>) : null}
      </div>
    )
  }

  return (
    <div style={pageStyle}>
      <button onClick={() => navigate(-1)} style={backBtn}>
        <ArrowLeft size={14} aria-hidden="true" /> Back
      </button>

      {/* One obvious way in to the draft enrollment form for THIS patient. Sits above the header
          band so it is present under both the custom and the default layout, rather than being
          duplicated into each. */}
      <PatientEnrollmentPdfButton patientId={id || ''} />

      {regionCards('header').length > 0 ? (
        renderHeaderBand(regionCards('header'))
      ) : (
      <div style={headerCard}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <h1 style={titleStyle}>
            {patient.display_name as string}
            {patient.is_animal ? <PawPrint size={20} style={{ marginLeft: 8, color: '#a16207', verticalAlign: 'middle' }} /> : null}
          </h1>
          {showCore('status') && patient.status && <span style={statusBadge}>{patient.status as string}</span>}
        </div>
        <div style={{ display: 'flex', gap: 18, marginTop: 8, fontSize: 13, color: 'var(--color-text-soft)', flexWrap: 'wrap' }}>
          {showCore('date_of_birth') && <span><strong style={{ color: 'var(--color-text)' }}>DOB</strong> {formatDob(patient.date_of_birth as string)}</span>}
          {showCore('gender') && <span><strong style={{ color: 'var(--color-text)' }}>Gender</strong> {(patient.gender as string) ?? (patient.sex as string) ?? '—'}</span>}
          {showCore('primary_phone') && patient.primary_phone && <span><Phone size={12} style={{ verticalAlign: 'middle' }} /> {formatPhone(patient.primary_phone as string)}</span>}
          {showOpt('address') && <span><strong style={{ color: 'var(--color-text)' }}>Address</strong> {[patient.address_line, patient.city, patient.state_code, patient.postal_code].filter(Boolean).join(', ') || '—'}</span>}
        </div>
        {showCore('allergies') && (patient.allergies && patient.allergies !== 'None') ? (
          <div style={{ ...alertBox, background: '#fee2e2', borderColor: '#fecaca', color: '#991b1b' }}>
            <AlertTriangle size={14} aria-hidden="true" />
            <strong>Allergies:</strong> {patient.allergies as string}
            {patient.allergy_alert_type && <span style={mutedSm}> ({patient.allergy_alert_type as string})</span>}
          </div>
        ) : null}
        {patient.comment_critical ? (
          <div style={{ ...alertBox, background: '#fee2e2', borderColor: '#fecaca', color: '#991b1b' }}>
            <AlertTriangle size={14} aria-hidden="true" />
            <strong>Critical:</strong> {patient.comment_critical as string}
          </div>
        ) : null}
        {patient.comment_pos ? (
          <div style={{ ...alertBox, background: '#ecfeff', borderColor: '#a5f3fc', color: '#0e7490' }}>
            <strong>Delivery:</strong> {patient.comment_pos as string}
          </div>
        ) : null}
        {patient.comment_informational ? (
          <div style={{ ...alertBox, background: '#f3f4f6', borderColor: '#e5e7eb', color: '#374151' }}>
            <strong>Note:</strong> {patient.comment_informational as string}
          </div>
        ) : null}
      </div>
      )}

      <div style={gridStyle}>
        {layout?.source === 'custom' ? renderCards(regionCards('body')) : (<>
        <Section icon={User} title="Demographics">
          <Field label="Name">{patient.display_name as string}</Field>
          <Field label="First name">{(patient.first_name as string) ?? '—'}</Field>
          <Field label="Last name">{(patient.last_name as string) ?? '—'}</Field>
          <Field label="Date of birth">{formatDob(patient.date_of_birth as string)}</Field>
          <Field label="Gender">{(patient.gender as string) ?? (patient.sex as string) ?? '—'}</Field>
          {patient.height_inches != null && <Field label="Height">{`${patient.height_inches}″`}</Field>}
          {patient.weight_ounces != null && <Field label="Weight">{`${(Number(patient.weight_ounces) / 16).toFixed(1)} lb`}</Field>}
          {patient.is_animal ? <Field label="Patient type">Veterinary</Field> : null}
        </Section>

        <Section icon={Phone} title="Contact & preferences">
          <Field label="Phone">{formatPhone(patient.primary_phone as string)}</Field>
          <Field label="Email">{(patient.email as string) ?? '—'}</Field>
          {/* ASP #26: alternate emails a CareRx pharmacy adds (mig 420) — PioneerRx's email is untouched. */}
          {showOtherEmails && id && <OtherEmailsSection subjectType="patient" subjectId={id} />}
          <Field label="Address">{
            [patient.address_line, patient.city, patient.state_code, patient.postal_code]
              .filter(Boolean).join(', ') || '—'
          }</Field>
          <Field label="Language">{(patient.language_text as string) ?? '—'}</Field>
          <Field label="Delivery">{(patient.delivery_method as string) ?? '—'}</Field>
          {/* D-SOC-1: live match of the current Rx address against the Site of Care directory. */}
          {id && <PatientSiteOfCareFields patientId={id} />}
          <Field label="Autofill">{(patient.autofill_mode as string) ?? '—'}</Field>
          {patient.do_not_email ? <Field label="Do not email">Yes</Field> : null}
          {patient.do_not_text ? <Field label="Do not text">Yes</Field> : null}
        </Section>

        <Section icon={Heart} title="Clinical">
          <Field label="Allergies">{(patient.allergies as string) ?? 'None on file'}</Field>
          <Field label="Allergy alert">{(patient.allergy_alert_type as string) ?? '—'}</Field>
          {/* D-ING-1: real decomposed insurance (dbo.pay_methods) — payor + BIN/PCN/group per
              billing order; legacy single-string carrier only as fallback. */}
          {Array.isArray((data as any)?.pay_methods) && (data as any).pay_methods.length > 0 ? (
            (data as any).pay_methods.map((pm: any) => (
              <Field key={`pm-${pm.billing_order}`} label={pm.billing_order === 1 ? 'Insurance (primary)' : `Insurance #${pm.billing_order}`}>
                {pm.payor_name}
                {(pm.bin_number || pm.pcn || pm.plan_group_code)
                  ? ` — ${[pm.bin_number ? `BIN ${pm.bin_number}` : null, pm.pcn ? `PCN ${pm.pcn}` : null, pm.plan_group_code ? `Grp ${pm.plan_group_code}` : null].filter(Boolean).join(' · ')}`
                  : ''}
              </Field>
            ))
          ) : (
            <Field label="Insurance">{(patient.insurance_carrier as string) ?? 'Cash pay'}</Field>
          )}
          {patient.notes ? <Field label="Notes">{patient.notes as string}</Field> : null}
          {patient.comment_informational ? <Field label="Note">{patient.comment_informational as string}</Field> : null}
          {patient.patient_risk_score != null && <Field label="Risk score">{String(patient.patient_risk_score)}</Field>}
        </Section>

        <Section icon={Droplet} title="Infusion Information">
          {(patient.infusion_duration != null || patient.infusion_rate != null || patient.infusion_site != null || patient.infusion_access != null || patient.infusion_notes != null) ? (<>
            {patient.infusion_duration != null && <Field label="Infusion duration">{String(patient.infusion_duration)}</Field>}
            {patient.infusion_rate != null && <Field label="Infusion rate">{String(patient.infusion_rate)}</Field>}
            {patient.infusion_access != null && <Field label="Access type">{String(patient.infusion_access)}</Field>}
            {patient.infusion_site != null && <Field label="Site">{String(patient.infusion_site)}</Field>}
            {patient.infusion_notes != null && <Field label="Infusion notes">{String(patient.infusion_notes)}</Field>}
          </>) : <Field label="Infusion">No infusion information on file</Field>}
        </Section>
        </>)}
      </div>

      {layout?.source === 'custom' && regionCards('footer').length > 0 && (
        <div style={gridStyle}>{renderCards(regionCards('footer'))}</div>
      )}

      {/* Rx tables */}
      <h2 style={sectionH2}>Active prescriptions ({activeRx.length})</h2>
      <div style={cardStyle}>
        {activeRx.length === 0 ? (
          <EmptyState message="No active prescriptions." />
        ) : <RxTable rows={activeRx} onOpen={rxId => navigate(`/rx/${rxId}`)} layout={rxCols}
              onShip={hasPermission('shipping.create') ? (rxId => navigate(`/shipments?ship_rx=${rxId}`)) : undefined} />}
      </div>

      {/* Sprint 18 — deliveries for this patient (permission-gated, status only) */}
      <PatientShipmentsStrip patientId={id || ''} rxIds={rx.map(r => r.id)} />

      {/* CareRx — Plans of Care for this patient (unified clinical record) */}
      <PatientTherapyLanes patientId={id || ''} />
      <PatientCareRxStrip patientId={id || ''} patientName={patient.display_name as string} />

      {historicalRx.length > 0 && (
        <>
          <h2 style={sectionH2}>History ({historicalRx.length})</h2>
          <div style={cardStyle}>
            <RxTable rows={historicalRx} onOpen={rxId => navigate(`/rx/${rxId}`)} />
          </div>
        </>
      )}

      {hasPermission('patient.docs.read') && <PatientDocuments patientId={id || ''} />}

      <PatientComments patientId={id || ''} />
    </div>
  )
}

// D-VIEWCOLS-4 / BA-29: the Active Prescriptions table is def-driven from the pharmacy's
// patient_rx_list surface (builder: Patient view -> "Active prescriptions columns").
// Fallback = the historical fixed columns, so an unconfigured pharmacy renders identically.
const RX_TABLE_DEFAULT = ['rx_number', 'drug_name', 'sig', 'quantity_authorized', 'prescriber_name', 'state', 'payer_state', 'last_activity']
const RX_TABLE_LABELS: Record<string, string> = {
  rx_number: 'Rx #', drug_name: 'Drug', sig: 'Sig', quantity_authorized: 'Quantity',
  prescriber_name: 'Prescriber', state: 'Clinical', payer_state: 'Payer', last_activity: 'Last activity',
}

// D-UI-2: what each Rx cell SHOWS, as text, for the column funnels. The pill labels mirror
// ClinicalStatePill / PayerStatePill (StatePills.tsx does not export them); an unknown code falls
// back to the code itself, exactly as the pills do.
const RX_CLINICAL_LABELS: Record<string, string> = {
  draft: 'Draft', submitted: 'Submitted', verified: 'Verified', ready_to_fill: 'Ready to fill',
  dispensed: 'Dispensed', out_for_delivery: 'Out for delivery', delivered: 'Delivered',
  on_hold: 'On hold', cancelled: 'Cancelled', rejected: 'Rejected',
}
const RX_PAYER_LABELS: Record<string, string> = {
  not_required: 'No insurance', awaiting_insurance: 'Awaiting ins.', pa_required: 'PA needed',
  pa_pending: 'PA pending', pa_approved: 'PA approved', pa_denied: 'PA denied',
  insurance_rejected: 'Ins. rejected', cash_pay: 'Cash pay',
}
function rxCellText(r: api.PatientFullResponse['rx'][number], key: string): unknown {
  switch (key) {
    case 'rx_number': return r.rx_number
    case 'drug_name': return r.drug_name
    case 'sig': return r.sig
    case 'quantity_authorized': return [r.quantity_authorized, r.quantity_unit].filter(v => v != null && v !== '').join(' ')
    case 'prescriber_name': return r.prescriber_name
    case 'state': return RX_CLINICAL_LABELS[r.state] ?? r.state
    case 'payer_state': return RX_PAYER_LABELS[r.payer_state] ?? r.payer_state
    case 'last_activity': return r.last_state_change_at   // raw: the funnel compares by day
    default: {
      const raw = (r as any)[key]
      // A date stays raw (From/To by day); anything else is the formatted text the cell shows.
      return typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}/.test(raw) ? raw : formatCell(key, raw)
    }
  }
}

function RxTable({ rows, onOpen, layout, onShip }: { rows: api.PatientFullResponse['rx']; onOpen: (id: string) => void; layout?: api.RxViewLayout | null; onShip?: (id: string) => void }) {
  const card = layout?.cards?.find(c => c.card_key === 'list_columns')
  const cfgFields = (card?.fields ?? []).filter(f => f.visible)
  const defs = cfgFields.length > 0
    ? cfgFields.map(f => ({ key: f.owlrx_column, label: f.owlrx_label || RX_TABLE_LABELS[f.owlrx_column] || f.owlrx_column, custom: f.owlrx_table === 'custom', drugref: f.owlrx_table === 'drug_reference' }))
    : RX_TABLE_DEFAULT.map(k => ({ key: k, label: RX_TABLE_LABELS[k], custom: false, drugref: false }))
  // Values for PioneerRx/custom mapped columns (extraction store).
  const [cfv, setCfv] = useState<Record<string, Record<string, string | null>>>({})
  const hasCustom = defs.some(d => d.custom)
  // A patient can accumulate many Rx; render/resolve only the current page so the DOM
  // never holds thousands of rows (browser hang). Full-set sort/filter stays upstream.
  // D-UI-2: funnels filter the WHOLE Rx set, then the result is paged. Custom / drug-reference
  // columns are resolved per PAGE for display (below), so their values do not exist for the rows
  // off screen. Opening a funnel on one of them loads the values for every row, once; until
  // then the column reads the page's values, which is what the cells show.
  const defsSig = defs.map(d => `${d.key}:${d.label}:${d.custom ? 'c' : d.drugref ? 'd' : ''}`).join('|')
  const allIdsKey = rows.map(r => r.id).join(',')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allIds = useMemo(() => rows.map(r => r.id), [allIdsKey])
  const side = useRxSideValuesAll(allIds, { custom: hasCustom, drugref: defs.some(d => d.drugref) })
  const funnelCols = useMemo<FunnelColumn<api.PatientFullResponse['rx'][number]>[]>(
    () => defs.map(d => d.custom
      ? { key: d.key, label: d.label, onOpen: side.load,
          value: r => (side.cfv[r.id] ?? cfv[r.id])?.[`list_columns.${d.key}`] ?? null }
      : d.drugref
      ? { key: d.key, label: d.label, onOpen: side.load,
          value: r => (side.dr[String(r.id).toLowerCase()] as any)?.[d.key] ?? null }
      : { key: d.key, label: d.label, value: r => rxCellText(r, d.key), kind: d.key === 'sig' ? 'text' as const : undefined }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [defsSig, side.cfv, side.dr, side.load, cfv],
  )
  const fx = useTableFunnels(rows, funnelCols)
  const { page, setPage, pageSize, setPageSize, pageRows, total, totalPages } = usePaged(fx.rows, DEFAULT_PAGE_SIZE, fx.sig)
  const drugRefVals = useDrugRefValues(pageRows.map(r => r.id), defs.some(d => d.drugref))
  useEffect(() => {
    if (!hasCustom || pageRows.length === 0) return
    let dead = false
    api.batchCustomFieldValues('rx', pageRows.map(r => r.id)).then(r => { if (!dead) setCfv(r.values ?? {}) }).catch(() => {})
    return () => { dead = true }
  }, [hasCustom, pageRows])
  const subHidden = new Set<string>(((card?.config as any)?.subtext_hidden as string[] | undefined) ?? [])
  const cell = (r: api.PatientFullResponse['rx'][number], key: string) => {
    switch (key) {
      case 'rx_number': return (
        <td key={key} style={tdStyle}>
          <div style={{ fontFamily: 'monospace' }}>{r.rx_number ?? '—'}</div>
          {!subHidden.has('rx_number') && r.refill_number > 0 && <div style={mutedSm}>refill {r.refill_number}</div>}
        </td>)
      case 'drug_name': return (
        <td key={key} style={tdStyle}>
          <div style={{ fontWeight: 500 }}>
            {r.drug_name}
            {r.is_controlled && <span style={ctrlBadge}>{r.prescribed_dea_schedule ?? 'CTRL'}</span>}
          </div>
          {!subHidden.has('drug_name') && <div style={mutedSm}>{r.drug_strength} · {r.drug_form}</div>}
        </td>)
      case 'sig': return <td key={key} style={{ ...tdStyle, maxWidth: 240 }}><div style={truncStyle}>{r.sig}</div></td>
      case 'quantity_authorized': return <td key={key} style={tdStyle}>{r.quantity_authorized} {r.quantity_unit}</td>
      case 'prescriber_name': return <td key={key} style={tdStyle}>{r.prescriber_name}</td>
      case 'state': return <td key={key} style={tdStyle}>
        <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
          <ClinicalStatePill state={r.state} />
          {(r as any).ship_status === 'delivered' && (
            <span title="Download proof of delivery"
                  onClick={e => { e.stopPropagation(); api.openRxPodDoc(r.id).catch(() => { alert('Could not open the delivery document. Please try again.') }) }}
                  style={{ fontSize: 10.5, fontWeight: 600, padding: '2px 7px', borderRadius: 999, background: '#dcfce7', color: '#166534', cursor: 'pointer', textDecoration: 'underline dotted' }}>
              ✓ Delivered{(r as any).ship_delivered_at ? ` ${new Date((r as any).ship_delivered_at).toLocaleDateString()}` : ''}
            </span>
          )}
          {(r as any).ship_status && ['in_transit', 'out_for_delivery', 'label_purchased', 'created', 'picked_up'].includes((r as any).ship_status) && (
            <span style={{ fontSize: 10.5, fontWeight: 600, padding: '2px 7px', borderRadius: 999, background: '#dbeafe', color: '#1e40af' }}>⛟ In transit</span>
          )}
          {(r as any).ship_status === 'exception' && (
            <span style={{ fontSize: 10.5, fontWeight: 600, padding: '2px 7px', borderRadius: 999, background: '#fee2e2', color: '#991b1b' }}>⚠ Delivery issue</span>
          )}
        </span>
      </td>
      case 'payer_state': return <td key={key} style={tdStyle}><PayerStatePill state={r.payer_state} /></td>
      case 'last_activity': return <td key={key} style={tdStyle}>{relativeTime(r.last_state_change_at)}</td>
      default: {
        const d = defs.find(x => x.key === key)
        if (d?.drugref) return <td key={key} style={tdStyle}>{key === 'category' ? <CategoryBadge name={drugRefVals[r.id]?.category} color={drugRefVals[r.id]?.category_color} /> : <span style={{ fontSize: 12.5 }}>{String((drugRefVals[r.id] as any)?.[key] ?? '—')}</span>}</td>
        if (d?.custom) return <td key={key} style={tdStyle}><span style={{ fontSize: 12.5 }}>{cfv[r.id]?.[`list_columns.${key}`] ?? '—'}</span></td>
        // ASP-62/63: configured columns (NDC 5-4-2, phone/fax (xxx) xxx-xxxx, dates) format
        // via the central formatter instead of rendering raw; hover shows the raw value.
        return <td key={key} style={tdStyle} title={(r as any)[key] != null ? String((r as any)[key]) : undefined}>{formatCell(key, (r as any)[key])}</td>
      }
    }
  }
  return (
    <>
      <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} onPageSize={setPageSize} label="prescriptions" />
      {fx.chips}
      <table {...fx.tableProps} style={tableStyle}>
      <thead>
        <tr>{defs.map(d => <th key={d.key} style={thStyle}>{d.label} {fx.funnel(d.key)}</th>)}{onShip && <th style={{ ...thStyle, width: 34 }} aria-label="Quick ship" />}</tr>
      </thead>
      <tbody>
        {pageRows.map(r => (
          <tr key={r.id} style={trStyle} onClick={() => onOpen(r.id)} tabIndex={0}
              onKeyDown={e => { if (e.key === 'Enter') onOpen(r.id) }}
              role="button" aria-label={`Open Rx ${r.rx_number ?? r.id}`}>
            {defs.map(d => cell(r, d.key))}
            {onShip && (
              <td style={{ ...tdStyle, width: 34 }} onClick={e => e.stopPropagation()}>
                <button type="button" title="Quick ship" onClick={() => onShip(r.id)}
                  style={{ border: '1px solid var(--color-border)', background: 'none', borderRadius: 5, padding: '3px 5px', cursor: 'pointer', lineHeight: 0 }}>
                  <Truck size={13} />
                </button>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
      <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} showPageSize={false} label="prescriptions" />
    </>
  )
}

function Section({ icon: Icon, title, children }: { icon: any; title: string; children: React.ReactNode }) {
  return (
    <section style={{ background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: '1px solid var(--color-border)', background: 'var(--color-surface-alt)', color: 'var(--color-text-soft)' }}>
        <Icon size={14} aria-hidden="true" />
        <h3 style={{ margin: 0, fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>{title}</h3>
      </header>
      <div style={{ padding: '12px 16px 16px' }}>{children}</div>
    </section>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 10, padding: '4px 0', fontSize: 13, borderBottom: '1px solid #f5f5f4' }}>
      <div style={{ color: 'var(--color-text-mute)', fontSize: 12 }}>{label}</div>
      <div style={{ color: 'var(--color-text)', wordBreak: 'break-word' }}>{children || '—'}</div>
    </div>
  )
}

const pageStyle: React.CSSProperties = { maxWidth: 'var(--page-max, 1280px)', margin: '0 auto', padding: '24px 24px 48px' }
const backBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px',
  background: 'transparent', border: '1px solid var(--color-border)',
  borderRadius: 6, fontSize: 12, color: 'var(--color-text-soft)',
  cursor: 'pointer', fontFamily: 'inherit', marginBottom: 16,
}
const headerCard: React.CSSProperties = {
  background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border)',
  borderRadius: 8, padding: 20, marginBottom: 16,
}
const titleStyle: React.CSSProperties = {
  fontFamily: 'Georgia, "Source Serif Pro", serif',
  fontSize: 26, fontWeight: 700, margin: 0, letterSpacing: -0.5, display: 'inline-block',
}
const mutedSm: React.CSSProperties = { fontSize: 11, color: 'var(--color-text-mute)' }
const statusBadge: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, letterSpacing: 0.5, padding: '2px 8px',
  background: '#dcfce7', color: '#166534', borderRadius: 4,
}
const alertBox: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
  border: '1px solid', borderRadius: 4, fontSize: 13, marginTop: 12,
}
const gridStyle: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(360px, 100%), 1fr))',
  gap: 16, marginBottom: 16,
}
const sectionH2: React.CSSProperties = {
  fontSize: 16, fontWeight: 600, margin: '8px 0 12px', fontFamily: 'Georgia, serif',
}
const cardStyle: React.CSSProperties = {
  background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border)',
  borderRadius: 8, overflow: 'hidden', marginBottom: 16,
}
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 }
const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '10px 14px', background: 'var(--color-surface-alt)',
  borderBottom: '1px solid var(--color-border)', fontSize: 11, fontWeight: 600,
  letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--color-text-mute)',
}
const trStyle: React.CSSProperties = { borderBottom: '1px solid var(--color-border)', cursor: 'pointer' }
const tdStyle: React.CSSProperties = { padding: '10px 14px', verticalAlign: 'middle' }
const truncStyle: React.CSSProperties = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
const ctrlBadge: React.CSSProperties = {
  marginLeft: 6, fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
  padding: '1px 6px', background: '#fef3c7', color: '#854d0e', borderRadius: 3,
}

/** D-SOC-1 — Site of care rows (own component: hook below the page's early returns). */
function PatientSiteOfCareFields({ patientId }: { patientId: string }) {
  const q = usePatientSiteOfCare(patientId)
  if (q.isLoading || q.isError || !q.data?.available) return null
  const c = q.data.current
  const residential = c.facility_type === 'residential'
  const stampedElsewhere = q.data.rx_snapshots.filter(s => (s.facility_id ?? null) !== (c.facility_id ?? null))
  return (
    <>
      <Field label="Site of care">
        <span style={{ fontSize: 11.5, padding: '2px 9px', borderRadius: 20, background: residential ? '#F1EFE8' : '#fef3c7', color: residential ? '#444441' : '#92400e', fontWeight: 600 }}>
          {c.facility_type_label.split(' — ')[0]}
        </span>
        {!residential && <span style={{ marginLeft: 8 }}>{c.facility_name}{c.facility_phone ? ` · ${formatPhone(c.facility_phone)}` : ''}</span>}
      </Field>
      {!residential && c.contact_name && <Field label="Facility contact">{c.contact_name}{c.contact_phone ? ` · ${formatPhone(c.contact_phone)}` : ''}{c.contact_email ? ` · ${c.contact_email}` : ''}</Field>}
      {!residential && (c.operating_hours || c.delivery_cutoff_time) && <Field label="Hours / cutoff">{[c.operating_hours, c.delivery_cutoff_time ? `cutoff ${c.delivery_cutoff_time}` : null].filter(Boolean).join(' · ')}</Field>}
      {stampedElsewhere.length > 0 && (
        <Field label="Earlier Rx">
          <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
            {stampedElsewhere.map(s => `${s.rx} Rx at ${s.facility_name ?? s.facility_type_label.split(' — ')[0]}`).join(' · ')}
          </span>
        </Field>
      )}
    </>
  )
}
